<template>
  <div class="d-flex flex-column min-vh-100">
    <v-container class="flex-grow-1 d-flex">
      <v-row justify="center" class="w-100">
        <v-col cols="12" sm="10" md="8" lg="6">
          <div class="text-center mb-8 mt-8">
            <h1 class="text-primary weight-600">Import CMM Submission</h1>
            <p class="text-grey mt-2">Enter the CoverMyMeds submission details and upload supporting documents.</p>
          </div>

          <v-card class="pa-6">
            <v-card-text>
              <v-form ref="formRef" @submit.prevent="handleSubmit">
                <v-text-field
                  v-model="submissionKey"
                  label="CMM Submission Key"
                  placeholder="e.g. BXNQ2K3P"
                  :rules="[rules.required, rules.cmmKey]"
                  class="mb-2 lh--force-white-background" />

                <v-text-field
                  v-model="patientLastName"
                  label="Patient Last Name"
                  placeholder="Enter patient last name"
                  :rules="[rules.required]"
                  class="mb-2 lh--force-white-background" />

                <TextDateInput
                  v-model="patientDobDate"
                  label="Patient Date of Birth"
                  :rules="[rules.required]"
                  class="mb-2 lh--force-white-background" />

                <v-textarea
                  v-model="extraContextNotes"
                  label="Additional Context (optional)"
                  placeholder="e.g. medical history, vaccinations, payor requirements, requests for expedited review, etc."
                  rows="3"
                  maxlength="3500"
                  counter="3500"
                  class="mb-2 lh--force-white-background" />

                <div class="advanced mb-5">
                  <button
                    type="button"
                    class="advanced__toggle"
                    :aria-expanded="showAdvanced"
                    @click="showAdvanced = !showAdvanced">
                    <span class="advanced__heading">
                      <span class="text-subtitle-2 text-primary weight-600">Additional details</span>
                      <span class="text-caption text-medium-emphasis">
                        Optional — values CoverMyMeds may need that do not always import
                      </span>
                    </span>
                    <v-icon size="22" class="advanced__chevron" :class="{ 'advanced__chevron--open': showAdvanced }">
                      mdi-chevron-down
                    </v-icon>
                  </button>

                  <v-expand-transition>
                    <div v-show="showAdvanced" class="advanced__body">
                      <v-row>
                        <v-col cols="12" sm="6">
                          <label class="advanced__label">
                            Dosage form
                            <v-tooltip location="top" max-width="320" open-delay="100">
                              <template #activator="{ props }">
                                <v-icon v-bind="props" size="15" class="advanced__help">mdi-help-circle-outline</v-icon>
                              </template>
                              <span>
                                CoverMyMeds requires a dosage form (package size unit) to submit. It is often not
                                carried over from the pharmacy system or the prescription, so setting it here keeps the
                                submission from stalling.
                              </span>
                            </v-tooltip>
                          </label>
                          <v-combobox
                            v-model="packageSizeUnit"
                            :items="packageSizeUnits"
                            placeholder="e.g. Gram, Each, mL"
                            :rules="packageSizeUnits.length ? [(v) => valueInItemsRule(v, packageSizeUnits)] : []"
                            hide-details="auto"
                            class="lh--force-white-background" />
                        </v-col>

                        <v-col cols="12" sm="6">
                          <label class="advanced__label">
                            Patient phone number
                            <v-tooltip location="top" max-width="320" open-delay="100">
                              <template #activator="{ props }">
                                <v-icon v-bind="props" size="15" class="advanced__help">mdi-help-circle-outline</v-icon>
                              </template>
                              <span>
                                Plans frequently require a patient phone number. Adding it here avoids a follow-up when
                                CoverMyMeds did not capture one.
                              </span>
                            </v-tooltip>
                          </label>
                          <v-text-field
                            v-model="patientPhone"
                            type="tel"
                            placeholder="e.g. (555) 123-4567"
                            :rules="[usPhoneRule]"
                            hide-details="auto"
                            class="lh--force-white-background" />
                        </v-col>

                        <v-col cols="12" sm="6">
                          <label class="advanced__label">
                            Dispense as written (DAW)
                            <v-tooltip location="top" max-width="320" open-delay="100">
                              <template #activator="{ props }">
                                <v-icon v-bind="props" size="15" class="advanced__help">mdi-help-circle-outline</v-icon>
                              </template>
                              <span>
                                Set to Yes when the prescriber requires the brand be dispensed as written. Leave blank
                                to use the default (No).
                              </span>
                            </v-tooltip>
                          </label>
                          <v-select
                            v-model="daw"
                            :items="['Yes', 'No']"
                            clearable
                            placeholder="Default (No)"
                            hide-details="auto"
                            class="lh--force-white-background" />
                        </v-col>

                        <v-col cols="12" sm="6">
                          <label class="advanced__label">
                            Expedited review
                            <v-tooltip location="top" max-width="320" open-delay="100">
                              <template #activator="{ props }">
                                <v-icon v-bind="props" size="15" class="advanced__help">mdi-help-circle-outline</v-icon>
                              </template>
                              <span>
                                Request an urgent (within 24h) review for this order. Leave blank to use your account
                                default.
                              </span>
                            </v-tooltip>
                          </label>
                          <v-select
                            v-model="expeditedReview"
                            :items="expeditedOptions"
                            clearable
                            placeholder="Use default"
                            hide-details="auto"
                            class="lh--force-white-background" />
                        </v-col>
                      </v-row>
                    </div>
                  </v-expand-transition>
                </div>

                <h3 class="text-primary weight-600 mb-2">Upload Documents</h3>
                <div
                  v-for="(doc, index) in supplementalDocs"
                  :key="index"
                  class="doc-drop-zone mb-3 pa-3 rounded-lg d-flex align-center"
                  :class="{
                    'doc-drop-zone--active': dragOverIndex === index && !doc.links.length,
                    'doc-drop-zone--reject': dragOverIndex === index && doc.links.length,
                  }"
                  @dragover.prevent
                  @dragenter.prevent="onDragEnter(index)"
                  @dragleave.prevent="onDragLeave"
                  @drop.prevent="handleFileDrop(index, $event)">
                  <input
                    :ref="(el) => (fileInputRefs[index] = el)"
                    type="file"
                    accept=".pdf,.tif,.tiff,.jpg,.jpeg,.png"
                    style="display: none"
                    @change="handleFileSelect(index, $event)" />
                  <v-icon class="mr-3 flex-shrink-0" :color="doc.links.length ? 'success' : 'grey'">
                    {{ doc.links.length ? "mdi-check-circle" : "mdi-file-outline" }}
                  </v-icon>
                  <div class="flex-grow-1" style="min-width: 0">
                    <v-text-field
                      v-model="doc.label"
                      placeholder="No file selected"
                      variant="underlined"
                      density="compact"
                      hide-details
                      class="text-body-2" />
                    <div v-if="doc.error" class="text-caption text-error">{{ doc.error }}</div>
                  </div>
                  <v-btn
                    v-if="doc.links.length"
                    :href="doc.links[0]"
                    target="_blank"
                    icon="mdi-download"
                    variant="text"
                    size="small"
                    color="primary"
                    class="ml-1 flex-shrink-0" />
                  <v-btn
                    v-else
                    variant="tonal"
                    size="small"
                    color="primary"
                    prepend-icon="mdi-upload"
                    class="ml-2 flex-shrink-0"
                    :loading="doc.uploading"
                    @click="fileInputRefs[index]?.click()">
                    Upload
                  </v-btn>
                  <v-btn
                    icon="mdi-close"
                    variant="text"
                    size="x-small"
                    color="error"
                    class="ml-1 flex-shrink-0"
                    @click="supplementalDocs.splice(index, 1)" />
                </div>
                <v-btn
                  color="primary"
                  variant="outlined"
                  prepend-icon="mdi-plus"
                  class="mb-4"
                  @click="supplementalDocs.push({ label: null, links: [], file: null, uploading: false, error: null })">
                  Add Supplemental Document
                </v-btn>

                <ProviderDefaultCommunicationMethod
                  ref="providerDefaultCommunicationMethodRef"
                  v-model:enabled="forwardResponsesToProviderByEmail"
                  v-model:email="providerEmail"
                  :show-errors="importFormSubmitted"
                  class="mb-4" />

                <v-btn
                  color="primary"
                  block
                  size="large"
                  type="submit"
                  :loading="isSubmitting"
                  :disabled="isSubmitting || !!importedOrderKey">
                  Import Submission
                </v-btn>

                <v-alert v-if="errors.length > 0" type="error" variant="tonal" class="mt-4">
                  <div v-for="error in errors" :key="error">{{ error }}</div>
                </v-alert>

                <v-alert v-if="successMessage" type="success" variant="tonal" class="mt-4">
                  {{ successMessage }}
                </v-alert>

                <div v-if="importedOrderKey" class="d-flex ga-3 mt-4">
                  <v-btn color="primary" variant="outlined" @click="viewOrder">
                    {{ isDuplicate ? "View Existing Submission" : "View Order" }}
                  </v-btn>
                  <v-btn color="primary" variant="tonal" @click="resetForm">
                    {{ isDuplicate ? "Try a Different Key" : "Import Another" }}
                  </v-btn>
                </div>
              </v-form>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- No Documents Warning Dialog -->
    <v-dialog v-model="showNoDocsWarning" max-width="480" persistent>
      <v-card class="pa-2">
        <v-card-text class="text-center pt-6 pb-2">
          <v-icon size="48" color="warning" class="mb-4">mdi-file-alert-outline</v-icon>
          <h3 class="text-primary weight-600 mb-3">No Documents Attached</h3>
          <p class="text-body-1 text-medium-emphasis mb-2">
            This submission does not include any supporting documents. The order will not be processed until the
            required documentation has been provided.
          </p>
          <p class="text-body-2 text-medium-emphasis">
            Would you like to go back and upload documents, or proceed without them?
          </p>
        </v-card-text>
        <v-card-actions class="pa-4 pt-2">
          <v-spacer />
          <v-btn variant="outlined" color="primary" @click="showNoDocsWarning = false">Go Back</v-btn>
          <v-btn variant="flat" color="warning" :loading="isSubmitting" @click="confirmImportWithoutDocs">
            Import Anyway
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { orderInfoService } from "@/services/orderInfoService";
import { uploadService, validateFile, parseUploadError } from "@/services/uploadService";
import { tryParseApiErrors } from "@/utils";
import { valueInItemsRule, usPhoneRule } from "@/utils/form";
import { uuid } from "vue-uuid";
import TextDateInput from "@/components/TextDateInput.vue";
import ProviderDefaultCommunicationMethod from "@/components/ProviderDefaultCommunicationMethod.vue";
import { useRequirementsStore } from "@/stores/requirementsStore";
import { useDemoCmmImport } from "@/demo/useDemoCmmImport";

const route = useRoute();
const router = useRouter();
const requirementsStore = useRequirementsStore();
const formRef = ref(null);
const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024;

const submissionKey = ref("");
const patientLastName = ref("");
const patientDobDate = ref(null);
const extraContextNotes = ref("");
const patientPhone = ref("");
const packageSizeUnit = ref(null);
const daw = ref(null);
const expeditedReview = ref(null);
const showAdvanced = ref(true);

// Dosage-form options are the canonical DRUG_QUANTITY_UNITS list, served via client config.
const packageSizeUnits = computed(() => requirementsStore.clientConfig?.intake?.packageSizeUnits || []);
// Tri-state: Yes/No override, or blank to fall back to the client's default_expedited_review.
const expeditedOptions = [
  { title: "Yes", value: true },
  { title: "No", value: false },
];
const supplementalDocs = ref([{ label: null, links: [], file: null, uploading: false, error: null }]);
const fileInputRefs = ref({});
const isSubmitting = ref(false);
const errors = ref([]);
const successMessage = ref(null);
const importedOrderKey = ref(null);
const isDuplicate = ref(false);
const isReimport = ref(false);
const showNoDocsWarning = ref(false);
const dragOverIndex = ref(null);
const forwardResponsesToProviderByEmail = ref(false);
const providerEmail = ref(null);
const providerDefaultCommunicationMethodRef = ref(null);
const importFormSubmitted = ref(false);
let dragEnterCount = 0;

onMounted(async () => {
  const prefillKey = route.query.key;
  // Don't clobber a key the user already typed before this component mounted.
  if (typeof prefillKey === "string" && prefillKey.trim() && !submissionKey.value) {
    submissionKey.value = prefillKey.trim().toUpperCase();
  }
  // Ensure dosage-form options are available even if the sidebar hasn't loaded client config yet.
  // Non-fatal if it fails — the combobox still accepts free text and the backend validates.
  if (!requirementsStore.clientConfig) {
    try {
      await requirementsStore.refreshCurrentUserClientConfig();
    } catch (e) {
      // ignore — leave the dropdown empty rather than block the import form
    }
  }
});

const CMM_KEY_REGEX = /^[A-Z0-9]{6,8}$/;

const rules = {
  required: (v) => !!v || "This field is required",
  cmmKey: (v) => {
    if (!v) return true;
    const cleaned = v
      .replace(/^key\s*:\s*/i, "")
      .trim()
      .toUpperCase();
    return CMM_KEY_REGEX.test(cleaned) || "Enter a valid CMM key (6\u20138 letters/digits, e.g. BXNQ2K3P).";
  },
};

const hasUploadedDocs = () => supplementalDocs.value.some((doc) => doc.links.length > 0);
const isAnyUploadInProgress = () => supplementalDocs.value.some((doc) => doc.uploading);

const patientDobIso = computed(() => {
  const d = patientDobDate.value;
  if (!(d instanceof Date) || isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = (d.getMonth() + 1).toString().padStart(2, "0");
  const day = d.getDate().toString().padStart(2, "0");
  return `${y}-${m}-${day}`;
});

const onDragEnter = (index) => {
  dragEnterCount++;
  dragOverIndex.value = index;
};

const onDragLeave = () => {
  dragEnterCount--;
  if (dragEnterCount <= 0) {
    dragEnterCount = 0;
    dragOverIndex.value = null;
  }
};

const processFile = async (index, file) => {
  const doc = supplementalDocs.value[index];
  doc.error = null;
  if (!file) return;
  const validationError = validateFile(file, { maxSizeBytes: MAX_FILE_SIZE_BYTES });
  if (validationError) {
    doc.error = validationError;
    return;
  }
  const uploadKey = uuid.v4() + "/" + file.name;
  doc.uploading = true;
  try {
    const link = await uploadService.uploadFile(file, uploadKey);
    doc.links = [link];
    doc.label = file.name;
    doc.error = null;
  } catch (error) {
    doc.error = parseUploadError(error);
  } finally {
    doc.uploading = false;
  }
};

const handleFileDrop = async (index, event) => {
  dragOverIndex.value = null;
  dragEnterCount = 0;
  const doc = supplementalDocs.value[index];
  if (doc.links.length) {
    doc.error = "This document already has a file. Remove it first to upload a new one.";
    return;
  }
  const file = event.dataTransfer?.files?.[0];
  if (file) await processFile(index, file);
};

const handleFileSelect = async (index, event) => {
  const file = event.target.files[0];
  await processFile(index, file);
  event.target.value = "";
};

const handleSubmit = async () => {
  if (isSubmitting.value) return;
  errors.value = [];
  importFormSubmitted.value = true;

  const { valid } = await formRef.value.validate();
  if (!valid) return;
  if (!providerDefaultCommunicationMethodRef.value?.validate()) return;

  if (isAnyUploadInProgress()) {
    errors.value = ["Please wait for file uploads to complete before importing."];
    return;
  }

  if (!hasUploadedDocs()) {
    showNoDocsWarning.value = true;
    return;
  }

  await performSubmit();
};

const confirmImportWithoutDocs = async () => {
  if (isAnyUploadInProgress()) {
    showNoDocsWarning.value = false;
    errors.value = ["Please wait for file uploads to complete before importing."];
    return;
  }
  showNoDocsWarning.value = false;
  importFormSubmitted.value = true;
  if (!providerDefaultCommunicationMethodRef.value?.validate()) return;
  await performSubmit();
};

const performSubmit = async () => {
  submissionKey.value = submissionKey.value
    .replace(/^key\s*:\s*/i, "")
    .trim()
    .toUpperCase();
  const extraContext = extraContextNotes.value.trim();
  errors.value = [];
  successMessage.value = null;
  isSubmitting.value = true;

  try {
    const uploadedDocs = supplementalDocs.value
      .filter((doc) => doc.links.length > 0)
      .map((doc) => ({ label: doc.label, links: doc.links, context: "" }));
    const payload = {
      submission_key: submissionKey.value,
      patient_last_name: patientLastName.value,
      patient_dob: patientDobIso.value,
      patient_chart: uploadedDocs,
    };
    if (extraContext) {
      payload.extra_structured_information = { additional_context: extraContext };
    }
    const phone = patientPhone.value?.trim();
    if (phone) payload.patient_phone = phone;
    const unit = typeof packageSizeUnit.value === "string" ? packageSizeUnit.value.trim() : packageSizeUnit.value;
    if (unit) payload.package_size_unit = unit;
    if (daw.value) payload.daw = daw.value;
    // Send only when explicitly set (Yes/No); blank leaves it to the client default. Guard on null,
    // not falsiness, so an explicit "No" (false) is preserved.
    if (expeditedReview.value !== null && expeditedReview.value !== undefined) {
      payload.expedited_review_within24h = expeditedReview.value;
    }
    payload.forward_responses_to_provider_by_email = forwardResponsesToProviderByEmail.value;
    if (forwardResponsesToProviderByEmail.value) {
      payload.provider_email = providerEmail.value?.trim();
    }
    const response = await orderInfoService.importCMMSubmission(payload);
    const orderKey = response?.orderKey;
    if (!orderKey) {
      errors.value = ["Submission was created but no order key was returned. Please check All Submissions."];
      return;
    }
    importedOrderKey.value = orderKey;
    isReimport.value = !!response?.wasReimport;
    successMessage.value = isReimport.value
      ? "Patient information updated. Re-running the import with the corrected details."
      : "Submission imported successfully.";
  } catch (error) {
    errors.value = tryParseApiErrors(error);
    if (errors.value.length === 0) {
      errors.value = ["An unexpected error occurred. Please try again."];
    }
    // 409 duplicate-key: offer a link to the pre-existing submission
    const existingKey = error?.response?.data?.existing_order_key;
    if (error?.response?.status === 409 && existingKey) {
      importedOrderKey.value = existingKey;
      isDuplicate.value = true;
    }
  } finally {
    isSubmitting.value = false;
  }
};

const resetForm = () => {
  submissionKey.value = "";
  patientLastName.value = "";
  patientDobDate.value = null;
  extraContextNotes.value = "";
  patientPhone.value = "";
  packageSizeUnit.value = null;
  daw.value = null;
  expeditedReview.value = null;
  showAdvanced.value = true;
  supplementalDocs.value = [{ label: null, links: [], file: null, uploading: false, error: null }];
  errors.value = [];
  successMessage.value = null;
  importedOrderKey.value = null;
  isDuplicate.value = false;
  isReimport.value = false;
  showNoDocsWarning.value = false;
  dragOverIndex.value = null;
  forwardResponsesToProviderByEmail.value = false;
  providerEmail.value = null;
  importFormSubmitted.value = false;
  dragEnterCount = 0;
  formRef.value?.resetValidation();
};

const viewOrder = () => {
  router.push({ name: "check-my-coverage", params: { id: importedOrderKey.value } });
};

// Demo mode only: prefill the form and auto-advance into the live PA flow. No-op in production.
useDemoCmmImport({
  submissionKey,
  patientLastName,
  patientDobDate,
  extraContextNotes,
  supplementalDocs,
  importedOrderKey,
});
</script>

<style lang="scss" scoped>
@import "@/styles/_variables";

.doc-drop-zone {
  border: 1px solid $lh_dash_border;
  background: transparent;
  transition: border $lh_transition_fast, background $lh_transition_fast;

  &--active {
    border: 2px dashed $lh_color_blue;
    background: rgba($lh_color_blue, 0.04);
  }
}

.advanced {
  border-top: 1px solid $lh_dash_border;

  &__toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 0.75rem 0 0.25rem;
    background: transparent;
    border: none;
    cursor: pointer;
    text-align: left;
  }

  &__heading {
    display: flex;
    flex-direction: column;
    gap: 1px;
  }

  &__chevron {
    color: $lh_color_blue;
    transition: transform $lh_transition_fast;

    &--open {
      transform: rotate(180deg);
    }
  }

  &__body {
    padding-top: 0.5rem;
  }

  &__label {
    display: flex;
    align-items: center;
    margin-bottom: 4px;
    font-size: 0.8rem;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.7);
  }

  &__help {
    margin-left: 4px;
    color: rgba(0, 0, 0, 0.38);
    cursor: help;
  }
}
</style>
